import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useUpdateNodeInternals,
  type Edge,
  type Node,
  type OnInit,
  type OnEdgesChange,
  type OnNodesChange,
  type OnConnect,
  type Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import SystemMapEdge, { type SystemMapEdgeData } from './SystemMapEdge';
import {
  SystemMapEdgeActionsProvider,
  type MapEdgeEndpoint,
} from './SystemMapEdgeActionsContext';
import { SystemMapNodeActionsProvider } from './SystemMapNodeActionsContext';
import SystemMapAutomationNode, {
  type SystemMapAutomationNodeData,
} from './SystemMapAutomationNode';
import SystemMapAutomationStepNode, {
  type SystemMapAutomationStepNodeData,
} from './SystemMapAutomationStepNode';
import SystemMapNode, {
  type SystemMapConnectionHandle,
  type SystemMapNodeData,
} from './SystemMapNode';
import {
  getEdgeSourceAnchor,
  getEdgeTargetAnchor,
  getEdgeWaypoints,
  mergeEdgeMetadata,
  nodeMatchesQuery,
  updateMapEdge,
  updateMapViewport,
  updateNodePosition,
  updateNodeSize,
  type MapEdgeAnchor,
  type MapEdgeRow,
  type MapEdgeWaypoint,
  type MapNodeKind,
  type MapNodeRow,
  type MapViewport,
  type SystemMapRow,
} from '../../lib/systemMap';
import { nodeRowSize, SYSTEM_MAP_SNAP_GRID } from './systemMapNodeGeometry';

const nodeTypes = {
  systemMapNode: SystemMapNode,
  systemMapAutomationNode: SystemMapAutomationNode,
  systemMapAutomationStepNode: SystemMapAutomationStepNode,
};
const edgeTypes = { systemMapEdge: SystemMapEdge };

const EDGE_COLORS: Record<string, string> = {
  cta: '#0ea5e9',
  api: '#f97316',
  data: '#10b981',
  redirect: '#a855f7',
  deploy: '#64748b',
};

function useDebouncedCallback<T extends (...args: never[]) => void>(fn: T, delay: number) {
  const fnRef = useRef(fn);
  const timerRef = useRef<number>();
  fnRef.current = fn;
  return useCallback(
    (...args: Parameters<T>) => {
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => fnRef.current(...args), delay);
    },
    [delay],
  );
}

function visibleNodeIdsForKindFilters(
  rows: MapNodeRow[],
  kindFilters: Set<MapNodeKind>,
): Set<string> {
  if (!kindFilters || kindFilters.size === 0) return new Set();
  return new Set(rows.filter((row) => kindFilters.has(row.kind)).map((row) => row.id));
}

function buildConnectionHandlesForNode(
  nodeId: string,
  edgeRows: MapEdgeRow[],
  selectedEdgeId: string | null,
  selectedEndpoint: MapEdgeEndpoint | null,
): SystemMapConnectionHandle[] {
  const handles: SystemMapConnectionHandle[] = [];
  for (const edge of edgeRows) {
    if (edge.source_node_id === nodeId) {
      const anchor = getEdgeSourceAnchor(edge.metadata);
      handles.push({
        id: `source-${edge.id}`,
        type: 'source',
        side: anchor.side,
        offset: anchor.offset,
        active: edge.id === selectedEdgeId && selectedEndpoint !== 'target',
      });
    }
    if (edge.target_node_id === nodeId) {
      const anchor = getEdgeTargetAnchor(edge.metadata);
      handles.push({
        id: `target-${edge.id}`,
        type: 'target',
        side: anchor.side,
        offset: anchor.offset,
        active: edge.id === selectedEdgeId && selectedEndpoint !== 'source',
      });
    }
  }
  return handles;
}

function toFlowNodes(
  rows: MapNodeRow[],
  edgeRows: MapEdgeRow[],
  searchQuery: string,
  kindFilters: Set<MapNodeKind>,
  selectedEdgeId: string | null,
  selectedEndpoint: MapEdgeEndpoint | null,
  connectMode: boolean,
  connectSourceNodeId: string | null,
): Node[] {
  const visibleIds = visibleNodeIdsForKindFilters(rows, kindFilters);
  const hasSearch = searchQuery.trim().length > 0;

  return rows
    .filter((row) => visibleIds.has(row.id))
    .map((row) => {
    const matchesSearch = hasSearch ? nodeMatchesQuery(row, searchQuery) : true;
    const highlighted = hasSearch && matchesSearch;
    const dimmed = hasSearch && !matchesSearch;
    const { width, height } = nodeRowSize(row);

    if (row.kind === 'automation') {
      const workflow = row.annotations.automation;
      const data: SystemMapAutomationNodeData = {
        label: row.label,
        slug: row.slug,
        kind: row.kind,
        layer: row.layer,
        description: row.role_description,
        alias: workflow?.automationAlias ?? null,
        layout: workflow?.layout === 'chain' ? 'chain' : 'inline',
        steps: workflow?.steps ?? [],
        width,
        height,
        highlighted,
        dimmed,
        connectMode,
        isConnectSource: connectSourceNodeId === row.id,
        connectionHandles: buildConnectionHandlesForNode(
          row.id,
          edgeRows,
          selectedEdgeId,
          selectedEndpoint,
        ),
      };

      return {
        id: row.id,
        type: 'systemMapAutomationNode',
        position: { x: row.x, y: row.y },
        width,
        height,
        style: { width, height },
        data,
        selected: false,
      };
    }

    if (row.kind === 'automation_step') {
      const stepRef = row.annotations.automationStep;
      const data: SystemMapAutomationStepNodeData = {
        label: row.label,
        slug: row.slug,
        kind: row.kind,
        layer: row.layer,
        scheduleLabel: stepRef?.scheduleLabel ?? row.role_description ?? 'Step',
        showThumbnail: row.show_thumbnail,
        screenshotUrl: row.screenshot_url,
        width,
        height,
        highlighted,
        dimmed,
        connectMode,
        isConnectSource: connectSourceNodeId === row.id,
        connectionHandles: buildConnectionHandlesForNode(
          row.id,
          edgeRows,
          selectedEdgeId,
          selectedEndpoint,
        ),
      };

      return {
        id: row.id,
        type: 'systemMapAutomationStepNode',
        position: { x: row.x, y: row.y },
        width,
        height,
        style: { width, height },
        data,
        selected: false,
      };
    }

    const data: SystemMapNodeData = {
      label: row.label,
      slug: row.slug,
      kind: row.kind,
      layer: row.layer,
      showThumbnail: row.show_thumbnail,
      screenshotUrl: row.screenshot_url,
      width,
      height,
      highlighted,
      dimmed,
      connectMode,
      isConnectSource: connectSourceNodeId === row.id,
      connectionHandles: buildConnectionHandlesForNode(
        row.id,
        edgeRows,
        selectedEdgeId,
        selectedEndpoint,
      ),
    };

    return {
      id: row.id,
      type: 'systemMapNode',
      position: { x: row.x, y: row.y },
      width,
      height,
      style: { width, height },
      data,
      selected: false,
    };
  });
}

function toFlowEdges(
  rows: MapEdgeRow[],
  nodeRows: MapNodeRow[],
  kindFilters: Set<MapNodeKind>,
  selectedEdgeId: string | null,
  showConnectorLabels: boolean,
): Edge[] {
  const visibleIds = visibleNodeIdsForKindFilters(nodeRows, kindFilters);

  return rows
    .filter(
      (row) => visibleIds.has(row.source_node_id) && visibleIds.has(row.target_node_id),
    )
    .map((row) => {
    const data: SystemMapEdgeData = {
      label: row.label,
      edgeType: row.edge_type,
      waypoints: getEdgeWaypoints(row.metadata),
      sourceNodeId: row.source_node_id,
      targetNodeId: row.target_node_id,
      animated: row.edge_type === 'api' || row.edge_type === 'cta',
      stroke: EDGE_COLORS[row.edge_type] ?? '#94a3b8',
      showConnectorLabels,
    };
    return {
      id: row.id,
      source: row.source_node_id,
      target: row.target_node_id,
      sourceHandle: `source-${row.id}`,
      targetHandle: `target-${row.id}`,
      type: 'systemMapEdge',
      animated: data.animated,
      selected: row.id === selectedEdgeId,
      style: { stroke: data.stroke, strokeWidth: 1.5 },
      data,
    };
  });
}

interface SystemMapCanvasInnerProps {
  map: SystemMapRow;
  nodeRows: MapNodeRow[];
  edgeRows: MapEdgeRow[];
  searchQuery: string;
  kindFilters: Set<MapNodeKind>;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onSelectEdge: (edgeId: string | null) => void;
  onNodeRowUpdated: Dispatch<SetStateAction<MapNodeRow[]>>;
  onEdgeRowUpdated: Dispatch<SetStateAction<MapEdgeRow[]>>;
  selectedWaypointIndex: number | null;
  onSelectWaypoint: (index: number | null) => void;
  selectedEndpoint: MapEdgeEndpoint | null;
  onSelectEndpoint: (endpoint: MapEdgeEndpoint | null) => void;
  showConnectorLabels: boolean;
  snapToGrid: boolean;
  connectMode: boolean;
  connectSourceNodeId: string | null;
  onConnectSourceSelect: (nodeId: string | null) => void;
  onConnectPair: (sourceNodeId: string, targetNodeId: string) => void;
  fitAllSignal: number;
  goToNodeId: string | null;
  onGoToComplete: () => void;
}

function SystemMapCanvasInner({
  map,
  nodeRows,
  edgeRows,
  searchQuery,
  kindFilters,
  selectedNodeId,
  selectedEdgeId,
  onSelectNode,
  onSelectEdge,
  onNodeRowUpdated,
  onEdgeRowUpdated,
  selectedWaypointIndex,
  onSelectWaypoint,
  selectedEndpoint,
  onSelectEndpoint,
  showConnectorLabels,
  snapToGrid,
  connectMode,
  connectSourceNodeId,
  onConnectSourceSelect,
  onConnectPair,
  fitAllSignal,
  goToNodeId,
  onGoToComplete,
}: SystemMapCanvasInnerProps) {
  const { setViewport, fitView, getViewport, screenToFlowPosition } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const mapIdRef = useRef(map.id);
  const latestViewportRef = useRef<MapViewport | null>(map.viewport);
  const viewportRestoredRef = useRef(false);
  const readyToPersistRef = useRef(false);

  mapIdRef.current = map.id;

  const [nodes, setNodes] = useState<Node[]>(() =>
    toFlowNodes(
      nodeRows,
      edgeRows,
      searchQuery,
      kindFilters,
      selectedEdgeId,
      selectedEndpoint,
      connectMode,
      connectSourceNodeId,
    ),
  );
  const [edges, setEdges] = useState<Edge[]>(() =>
    toFlowEdges(edgeRows, nodeRows, kindFilters, selectedEdgeId, showConnectorLabels),
  );

  const nodeRowsRef = useRef(nodeRows);
  nodeRowsRef.current = nodeRows;

  const edgeRowsRef = useRef(edgeRows);
  edgeRowsRef.current = edgeRows;

  const prevEdgeIdsRef = useRef(new Set(edgeRows.map((row) => row.id)));
  useEffect(() => {
    const prev = prevEdgeIdsRef.current;
    for (const edge of edgeRows) {
      if (!prev.has(edge.id)) {
        updateNodeInternals(edge.source_node_id);
        updateNodeInternals(edge.target_node_id);
      }
    }
    prevEdgeIdsRef.current = new Set(edgeRows.map((row) => row.id));
  }, [edgeRows, updateNodeInternals]);

  const persistEdgeMetadataDebounced = useDebouncedCallback(
    (edgeId: string, metadata: MapEdgeRow['metadata']) => {
      void updateMapEdge(edgeId, { metadata }).catch(() => {
        /* silent */
      });
    },
    400,
  );

  const updateEdgeMetadata = useCallback(
    (edgeId: string, patch: Partial<MapEdgeRow['metadata']>) => {
      onEdgeRowUpdated((prev) =>
        prev.map((row) =>
          row.id === edgeId ? { ...row, metadata: mergeEdgeMetadata(row.metadata, patch) } : row,
        ),
      );
    },
    [onEdgeRowUpdated],
  );

  const handleWaypointsChange = useCallback(
    (edgeId: string, waypoints: MapEdgeWaypoint[]) => {
      setEdges((prev) =>
        prev.map((edge) =>
          edge.id === edgeId
            ? { ...edge, data: { ...(edge.data as SystemMapEdgeData), waypoints } }
            : edge,
        ),
      );
      updateEdgeMetadata(edgeId, { waypoints });
    },
    [updateEdgeMetadata],
  );

  const handleWaypointsCommit = useCallback(
    (edgeId: string, waypoints: MapEdgeWaypoint[]) => {
      const row = edgeRowsRef.current.find((r) => r.id === edgeId);
      persistEdgeMetadataDebounced(
        edgeId,
        mergeEdgeMetadata(row?.metadata ?? {}, { waypoints }),
      );
    },
    [persistEdgeMetadataDebounced],
  );

  const handleAnchorChange = useCallback(
    (edgeId: string, endpoint: MapEdgeEndpoint, anchor: MapEdgeAnchor) => {
      updateEdgeMetadata(
        edgeId,
        endpoint === 'source' ? { sourceAnchor: anchor } : { targetAnchor: anchor },
      );
    },
    [updateEdgeMetadata],
  );

  const handleAnchorCommit = useCallback(
    (edgeId: string, endpoint: MapEdgeEndpoint, anchor: MapEdgeAnchor) => {
      const row = edgeRowsRef.current.find((r) => r.id === edgeId);
      persistEdgeMetadataDebounced(
        edgeId,
        mergeEdgeMetadata(row?.metadata ?? {}, {
          [endpoint === 'source' ? 'sourceAnchor' : 'targetAnchor']: anchor,
        }),
      );
    },
    [persistEdgeMetadataDebounced],
  );

  const handleSelectWaypoint = useCallback(
    (edgeId: string, index: number | null) => {
      if (edgeId !== selectedEdgeId) return;
      onSelectWaypoint(index);
      if (index != null) onSelectEndpoint(null);
    },
    [onSelectEndpoint, onSelectWaypoint, selectedEdgeId],
  );

  const handleSelectEndpoint = useCallback(
    (edgeId: string, endpoint: MapEdgeEndpoint | null) => {
      if (edgeId !== selectedEdgeId) return;
      onSelectEndpoint(endpoint);
      if (endpoint != null) onSelectWaypoint(null);
    },
    [onSelectEndpoint, onSelectWaypoint, selectedEdgeId],
  );

  const getNodeRow = useCallback(
    (nodeId: string) => nodeRowsRef.current.find((row) => row.id === nodeId),
    [],
  );

  const edgeActions = {
    onWaypointsChange: handleWaypointsChange,
    onWaypointsCommit: handleWaypointsCommit,
    selectedWaypointIndex,
    onSelectWaypoint: handleSelectWaypoint,
    onAnchorChange: handleAnchorChange,
    onAnchorCommit: handleAnchorCommit,
    selectedEndpoint,
    onSelectEndpoint: handleSelectEndpoint,
    getNodeRow,
  };

  const handleResizeEnd = useCallback(
    (nodeId: string, width: number, height: number) => {
      const roundedWidth = Math.round(width);
      const roundedHeight = Math.round(height);

      setNodes((prev) =>
        prev.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                width: roundedWidth,
                height: roundedHeight,
                style: { ...node.style, width: roundedWidth, height: roundedHeight },
                data: {
                  ...(node.data as SystemMapNodeData),
                  width: roundedWidth,
                  height: roundedHeight,
                },
              }
            : node,
        ),
      );

      void updateNodeSize(nodeId, roundedWidth, roundedHeight).catch(() => {
        /* silent */
      });
      onNodeRowUpdated((prev) =>
        prev.map((row) =>
          row.id === nodeId ? { ...row, width: roundedWidth, height: roundedHeight } : row,
        ),
      );
      updateNodeInternals(nodeId);
    },
    [onNodeRowUpdated, updateNodeInternals],
  );

  const nodeActions = { onResizeEnd: handleResizeEnd };

  const markReadyToPersist = useCallback((viewport: MapViewport) => {
    latestViewportRef.current = viewport;
    readyToPersistRef.current = true;
  }, []);

  const persistViewportNow = useCallback((viewport: MapViewport) => {
    latestViewportRef.current = viewport;
    void updateMapViewport(mapIdRef.current, viewport).catch(() => {
      /* layout still works locally */
    });
  }, []);

  const persistViewportDebounced = useDebouncedCallback((viewport: MapViewport) => {
    persistViewportNow(viewport);
  }, 400);

  const handleViewportChange = useCallback(
    (viewport: Viewport) => {
      if (!readyToPersistRef.current) return;
      const next = { x: viewport.x, y: viewport.y, zoom: viewport.zoom };
      latestViewportRef.current = next;
      persistViewportDebounced(next);
    },
    [persistViewportDebounced],
  );

  const restoreViewport = useCallback(() => {
    if (viewportRestoredRef.current) return;
    viewportRestoredRef.current = true;
    if (map.viewport) {
      markReadyToPersist(map.viewport);
      void setViewport(map.viewport, { duration: 0 });
    } else {
      void fitView({ padding: 0.15, duration: 0 }).then(() => {
        const vp = getViewport();
        markReadyToPersist({ x: vp.x, y: vp.y, zoom: vp.zoom });
      });
    }
  }, [map.viewport, setViewport, fitView, getViewport, markReadyToPersist]);

  const onInit: OnInit = useCallback(() => {
    restoreViewport();
  }, [restoreViewport]);

  useEffect(() => {
    const flush = () => {
      if (latestViewportRef.current) {
        void updateMapViewport(mapIdRef.current, latestViewportRef.current);
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, []);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setNodes((current) => applyNodeChanges(changes, current)),
    [],
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((current) => applyEdgeChanges(changes, current)),
    [],
  );

  useEffect(() => {
    setNodes((prev) => {
      const prevById = new Map(prev.map((node) => [node.id, node]));
      return toFlowNodes(
        nodeRows,
        edgeRows,
        searchQuery,
        kindFilters,
        selectedEdgeId,
        selectedEndpoint,
        connectMode,
        connectSourceNodeId,
      ).map((node) => {
        const existing = prevById.get(node.id);
        const width = existing?.width ?? node.width;
        const height = existing?.height ?? node.height;
        return {
          ...node,
          position: existing?.position ?? node.position,
          width,
          height,
          style: { width, height },
          data: {
            ...(node.data as SystemMapNodeData),
            width: width ?? (node.data as SystemMapNodeData).width,
            height: height ?? (node.data as SystemMapNodeData).height,
          },
          selected: node.id === selectedNodeId,
        };
      });
    });
  }, [nodeRows, edgeRows, searchQuery, kindFilters, selectedNodeId, selectedEdgeId, selectedEndpoint, connectMode, connectSourceNodeId]);

  const handleConnect: OnConnect = useCallback(
    (connection) => {
      if (!connectMode || !connection.source || !connection.target) return;
      if (connection.source === connection.target) {
        return;
      }
      onConnectPair(connection.source, connection.target);
    },
    [connectMode, onConnectPair],
  );

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (connectMode) {
        onSelectEdge(null);
        onSelectWaypoint(null);
        onSelectEndpoint(null);

        if (!connectSourceNodeId) {
          onConnectSourceSelect(node.id);
          onSelectNode(node.id);
          return;
        }

        if (connectSourceNodeId === node.id) {
          onConnectSourceSelect(null);
          onSelectNode(null);
          return;
        }

        onConnectPair(connectSourceNodeId, node.id);
        return;
      }

      onSelectEdge(null);
      onSelectWaypoint(null);
      onSelectEndpoint(null);
      onSelectNode(node.id);
    },
    [
      connectMode,
      connectSourceNodeId,
      onConnectPair,
      onConnectSourceSelect,
      onSelectEdge,
      onSelectEndpoint,
      onSelectNode,
      onSelectWaypoint,
    ],
  );

  const handlePaneClick = useCallback(() => {
    if (connectMode) {
      onConnectSourceSelect(null);
      onSelectNode(null);
      return;
    }
    onSelectNode(null);
    onSelectEdge(null);
    onSelectWaypoint(null);
    onSelectEndpoint(null);
  }, [connectMode, onConnectSourceSelect, onSelectEdge, onSelectEndpoint, onSelectNode, onSelectWaypoint]);

  useEffect(() => {
    setEdges((prev) => {
      const selected = new Set(prev.filter((e) => e.selected).map((e) => e.id));
      return toFlowEdges(edgeRows, nodeRows, kindFilters, selectedEdgeId, showConnectorLabels).map((edge) => ({
        ...edge,
        selected: edge.id === selectedEdgeId || selected.has(edge.id),
        data: {
          ...(edge.data as SystemMapEdgeData),
          showConnectorLabels,
          waypoints: getEdgeWaypoints(
            edgeRows.find((row) => row.id === edge.id)?.metadata ?? {},
          ),
        },
      }));
    });
  }, [edgeRows, nodeRows, kindFilters, selectedEdgeId, showConnectorLabels]);

  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      void updateNodePosition(node.id, node.position.x, node.position.y).catch(() => {
        /* silent */
      });
      onNodeRowUpdated((prev) =>
        prev.map((row) =>
          row.id === node.id ? { ...row, x: node.position.x, y: node.position.y } : row,
        ),
      );
    },
    [onNodeRowUpdated],
  );

  useEffect(() => {
    if (!fitAllSignal) return;
    void fitView({ padding: 0.15, duration: 300 }).then(() => {
      const vp = getViewport();
      persistViewportNow({ x: vp.x, y: vp.y, zoom: vp.zoom });
    });
  }, [fitAllSignal, fitView, getViewport, persistViewportNow]);

  useEffect(() => {
    if (!goToNodeId) return;
    const target = nodeRows.find((n) => n.id === goToNodeId);
    if (!target) {
      onGoToComplete();
      return;
    }
    const nextVp = { x: -target.x + 200, y: -target.y + 120, zoom: 1.2 };
    void setViewport(nextVp, { duration: 300 });
    persistViewportNow(nextVp);
    onSelectNode(goToNodeId);
    onGoToComplete();
  }, [goToNodeId, nodeRows, onGoToComplete, onSelectNode, setViewport, persistViewportNow]);

  return (
    <SystemMapEdgeActionsProvider value={edgeActions}>
      <SystemMapNodeActionsProvider value={nodeActions}>
        <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultViewport={map.viewport ?? undefined}
        onInit={onInit}
        onViewportChange={handleViewportChange}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={handleNodeDragStop}
        onConnect={handleConnect}
        onNodeClick={handleNodeClick}
        onEdgeClick={(_event, edge) => {
          if (connectMode) return;
          onSelectNode(null);
          onSelectWaypoint(null);
          onSelectEndpoint(null);
          onSelectEdge(edge.id);
        }}
        onPaneClick={handlePaneClick}
        nodesDraggable={!connectMode}
        nodesConnectable={connectMode}
        snapToGrid={snapToGrid}
        snapGrid={SYSTEM_MAP_SNAP_GRID}
        elementsSelectable
        edgesFocusable
        minZoom={0.15}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        className="bg-gray-50 dark:bg-gray-950"
      >
        <Background gap={20} size={1} className="!bg-gray-50 dark:!bg-gray-950" />
        <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !shadow-sm" />
      </ReactFlow>
      </SystemMapNodeActionsProvider>
    </SystemMapEdgeActionsProvider>
  );
}

export interface SystemMapCanvasProps {
  map: SystemMapRow;
  nodeRows: MapNodeRow[];
  edgeRows: MapEdgeRow[];
  searchQuery: string;
  kindFilters: Set<MapNodeKind>;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onSelectEdge: (edgeId: string | null) => void;
  onNodeRowUpdated: Dispatch<SetStateAction<MapNodeRow[]>>;
  onEdgeRowUpdated: Dispatch<SetStateAction<MapEdgeRow[]>>;
  selectedWaypointIndex: number | null;
  onSelectWaypoint: (index: number | null) => void;
  selectedEndpoint: MapEdgeEndpoint | null;
  onSelectEndpoint: (endpoint: MapEdgeEndpoint | null) => void;
  showConnectorLabels: boolean;
  snapToGrid: boolean;
  connectMode: boolean;
  connectSourceNodeId: string | null;
  onConnectSourceSelect: (nodeId: string | null) => void;
  onConnectPair: (sourceNodeId: string, targetNodeId: string) => void;
  fitAllSignal: number;
  goToNodeId: string | null;
  onGoToComplete: () => void;
}

export default function SystemMapCanvas(props: SystemMapCanvasProps) {
  return (
    <div className="flex-1 min-h-0 relative">
      <ReactFlowProvider>
        <SystemMapCanvasInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
